import type { IModelConnection } from "@itwin/core-frontend";

type EnumLookup = {
  byNumber: Map<number, string>;
  byString: Map<string, string>;
};

const enumLookupCache = new WeakMap<
  IModelConnection,
  Map<string, Promise<EnumLookup | undefined>>
>();

function makeCacheKey(className: string, propertyName: string): string {
  return `${className.toLowerCase()}.${propertyName.toLowerCase()}`;
}

function getModelCache(
  iModel: IModelConnection
): Map<string, Promise<EnumLookup | undefined>> {
  const existing = enumLookupCache.get(iModel);
  if (existing) return existing;

  const created = new Map<string, Promise<EnumLookup | undefined>>();
  enumLookupCache.set(iModel, created);
  return created;
}

function enumDisplayText(input: {
  name: string;
  label?: string;
}): string {
  const label = input.label?.trim();
  return label && label.length > 0 ? label : input.name;
}

function classNameToAlternateLookup(className: string): string | undefined {
  const dotIndex = className.indexOf(".");
  if (dotIndex <= 0) return undefined;
  return `${className.slice(0, dotIndex)}:${className.slice(dotIndex + 1)}`;
}

async function buildEnumLookup(
  iModel: IModelConnection,
  className: string,
  propertyName: string
): Promise<EnumLookup | undefined> {
  try {
    const schemaContext = (iModel as any)?.schemaContext;
    if (!schemaContext || typeof schemaContext.getSchemaItem !== "function") {
      return undefined;
    }

    const alternateClassName = classNameToAlternateLookup(className);
    const schemaItem =
      (await schemaContext.getSchemaItem(className)) ??
      (alternateClassName
        ? await schemaContext.getSchemaItem(alternateClassName)
        : undefined);
    if (!schemaItem || typeof (schemaItem as any).getProperty !== "function") {
      return undefined;
    }

    const property = await (schemaItem as any).getProperty(propertyName);
    if (
      !property ||
      typeof property.isEnumeration !== "function" ||
      !property.isEnumeration() ||
      !property.enumeration
    ) {
      return undefined;
    }

    const enumeration = await property.enumeration;
    const enumerators = (enumeration as any)?.enumerators;
    if (!Array.isArray(enumerators)) return undefined;

    const byNumber = new Map<number, string>();
    const byString = new Map<string, string>();

    for (const enumerator of enumerators as Array<any>) {
      const display = enumDisplayText({
        name: String(enumerator?.name ?? ""),
        label: enumerator.label,
      });
      if (!display) continue;

      if (typeof enumerator.value === "number") {
        byNumber.set(enumerator.value, display);
      } else if (typeof enumerator.value === "string") {
        byString.set(enumerator.value, display);
      }
    }

    if (byNumber.size === 0 && byString.size === 0) {
      return undefined;
    }

    return { byNumber, byString };
  } catch {
    return undefined;
  }
}

async function getEnumLookup(
  iModel: IModelConnection,
  className: string,
  propertyName: string
): Promise<EnumLookup | undefined> {
  const cache = getModelCache(iModel);
  const cacheKey = makeCacheKey(className, propertyName);
  const existing = cache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const created = buildEnumLookup(iModel, className, propertyName);
  cache.set(cacheKey, created);
  return created;
}

export async function resolveEnumDisplayValue(
  iModel: IModelConnection,
  className: string,
  propertyName: string,
  rawValue: unknown
): Promise<unknown> {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return rawValue;
  }

  const lookup = await getEnumLookup(iModel, className, propertyName);
  if (!lookup) return rawValue;

  if (typeof rawValue === "number") {
    return lookup.byNumber.get(rawValue) ?? rawValue;
  }

  if (typeof rawValue === "string") {
    const direct = lookup.byString.get(rawValue);
    if (direct) return direct;

    const asNumber = Number(rawValue);
    if (Number.isFinite(asNumber)) {
      return lookup.byNumber.get(asNumber) ?? rawValue;
    }
  }

  return rawValue;
}
